import { ScreenType, useScreenType } from "@redotech/react-util/screen";
import { usePickupModals } from "@redotech/redo-customer-portal-shared/hooks/useReturnConfirmation/use-pickup-modals";
import { StepType } from "@redotech/redo-customer-portal-shared/hooks/useReturnConfirmation/use-steps";
import { useTranslation } from "@redotech/redo-i18n";
import { Resource } from "@redotech/redo-model/localization/resource";
import { ReturnWarning } from "@redotech/redo-model/return";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Divider } from "@redotech/redo-web/divider";
import { Flex } from "@redotech/redo-web/flex";
import { AlertsCard } from "../components/alerts-card";
import { DeadlineCard } from "../components/deadline-card";
import { ManualLabelCard } from "../components/manual-label-card";
import { ManualLabelPackingSlipButtons } from "../components/manual-label-packing-slip-buttons";
import { MaybeDisplayHtml } from "../components/maybe-display-html";
import {
  ReturnStepsCard,
  ReturnStepsSearchCallbacks,
} from "../components/return-steps-card";
import { TopCard } from "../components/top-card";
import { WarningsCard } from "../components/warnings-card";
import * as detailCss from "../confirmation.module.css";
import {
  ReturnConfirmationDisplayManualLabelData,
  ReturnConfirmationDisplayManualLabelInfo,
  ReturnConfirmationDisplayReturnHeader,
} from "./types";

export interface ConfirmationStatusSectionProps
  extends ReturnStepsSearchCallbacks {
  returnId: string;

  returnHeader: ReturnConfirmationDisplayReturnHeader;
  customSuccessMessage: string | undefined;

  warnings: ReturnWarning[] | undefined;
  manualLabelInfo: ReturnConfirmationDisplayManualLabelInfo;
  manualLabelData?: ReturnConfirmationDisplayManualLabelData | null;

  showReturnSteps: boolean;
  steps: StepType[] | undefined;
  stepResource: Resource | undefined;
  pickupModals: ReturnType<typeof usePickupModals>;
  shouldAutoOpenBoxlessLocations: boolean;

  showDeadline: boolean | undefined;
  deadlineDays: number;

  showCancelButton: boolean;
  onCancelClick: () => void;
  onOpenUrl: (url: string) => void;
}

export function ConfirmationStatusSection({
  returnId,
  returnHeader,
  customSuccessMessage,
  warnings,
  manualLabelInfo,
  manualLabelData,
  showReturnSteps,
  steps,
  stepResource,
  pickupModals,
  shouldAutoOpenBoxlessLocations,
  showDeadline,
  deadlineDays,
  showCancelButton,
  onCancelClick,
  onOpenUrl,
  searchCarrierLocations,
  searchInStoreLocations,
  searchBlueYonderLocations,
  searchDropoffLocations,
  searchReturnBearLocations,
}: ConfirmationStatusSectionProps) {
  const { t } = useTranslation();
  const screen = useScreenType();
  const shouldShowDesktopView = screen === ScreenType.DESKTOP;

  return (
    <Flex
      className={shouldShowDesktopView ? detailCss.outlinedCard : undefined}
      flexDirection="column"
      gap="2xl"
      pb={shouldShowDesktopView ? "3xl" : "none"}
      pt="3xl"
      px={shouldShowDesktopView ? "3xl" : "none"}
    >
      <TopCard
        action={returnHeader.onClick}
        actionTitle={returnHeader.onClickTitle}
        cancelButton={
          showCancelButton ? (
            <RedoButton
              hierarchy="secondary"
              onClick={onCancelClick}
              size="sm"
              text={t("Cancel return")}
            />
          ) : undefined
        }
        status={returnHeader.status}
        subtitle={returnHeader.subtitle}
        title={returnHeader.title}
      />

      {customSuccessMessage && <MaybeDisplayHtml html={customSuccessMessage} />}

      <AlertsCard alerts={returnHeader.alerts} />

      <Flex data-ui="steps-section" flexDirection="column" gap="lg">
        <Divider />
        {warnings && warnings.length > 0 && (
          <WarningsCard warnings={warnings} />
        )}
        {manualLabelInfo.showManualLabel && (
          <ManualLabelCard instructions={manualLabelInfo.instructions} />
        )}
        {showReturnSteps && (
          <ReturnStepsCard
            copyResource={stepResource}
            onOpenUrl={onOpenUrl}
            pickupModals={pickupModals}
            returnId={returnId}
            searchBlueYonderLocations={searchBlueYonderLocations}
            searchCarrierLocations={searchCarrierLocations}
            searchDropoffLocations={searchDropoffLocations}
            searchInStoreLocations={searchInStoreLocations}
            searchReturnBearLocations={searchReturnBearLocations}
            shouldAutoOpenBoxlessLocations={shouldAutoOpenBoxlessLocations}
            steps={steps}
          />
        )}
        {manualLabelInfo.showManualLabel && manualLabelData && (
          <ManualLabelPackingSlipButtons
            manualLabelData={manualLabelData}
            onOpenUrl={onOpenUrl}
          />
        )}
        {showDeadline && <DeadlineCard deadlineDays={deadlineDays} />}
        {!shouldShowDesktopView && <Divider />}
      </Flex>
    </Flex>
  );
}
